import { Component, useState } from "react";
import { AlertTriangle, RefreshCw, Copy, Check, ChevronDown, ChevronRight } from "lucide-react";
import { reportError } from "@/lib/errorReporter";

/**
 * ErrorBoundary — captura erros de renderização React.
 *
 * Uso:
 *   <ErrorBoundary>            → fallback padrão (tela cheia)
 *   <ErrorBoundary inline>     → fallback compacto (dentro de layout)
 *
 * Props:
 *   - children: conteúdo protegido
 *   - inline: se true, mostra fallback compacto em vez de tela cheia
 *   - onError: callback opcional (error, errorInfo) para logging externo
 *
 * Instrumentação:
 *   - Loga `[ErrorBoundary]` no console com error.name, message, stack e componentStack
 *   - Guarda os últimos erros em sessionStorage (`tw:lastErrors`) para inspeção
 *   - Expõe `window.__twErrors` para inspeção imediata no DevTools
 *   - O fallback exibe detalhes copiáveis para diagnóstico remoto
 */

const STORAGE_KEY = "tw:lastErrors";
const MAX_STORED = 10;

function pushErrorToStorage(entry) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.unshift(entry);
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_STORED)));
  } catch {
    /* ignore quota / parse errors */
  }
  try {
    if (typeof window !== "undefined") {
      window.__twErrors = window.__twErrors || [];
      window.__twErrors.unshift(entry);
      window.__twErrors = window.__twErrors.slice(0, MAX_STORED);
    }
  } catch {
    /* ignore */
  }
}

export function recordGlobalError(source, error, extra = {}) {
  const entry = {
    source,
    ts: new Date().toISOString(),
    url: typeof window !== "undefined" ? window.location?.href : undefined,
    name: error?.name || "Error",
    message: error?.message || String(error),
    stack: error?.stack || null,
    ...extra,
  };
  console.error(`[ErrorBoundary:${source}]`, entry);
  pushErrorToStorage(entry);

  // Send to backend for CloudWatch logging
  reportError(source, error, extra);

  return entry;
}

// ── Test helper ──────────────────────────────────────────────────────────────
// Expose a global function for testing error reporting from the console:
//   window.__twTestError('my test message')
if (typeof window !== "undefined") {
  window.__twTestError = (message = "Test error from console") => {
    const error = new Error(message);
    recordGlobalError("console.test", error, { manual: true });
    console.log("[ErrorBoundary] Test error sent. Check API logs or CloudWatch.");
    return "Error sent!";
  };
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    recordGlobalError("boundary", error, {
      inline: Boolean(this.props.inline),
      componentStack: errorInfo?.componentStack || null,
    });
    this.props.onError?.(error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const details = buildErrorDetails(this.state.error, this.state.errorInfo);

    if (this.props.inline) {
      return (
        <InlineFallback onReset={this.handleReset} details={details} />
      );
    }

    return (
      <FullPageFallback onReload={this.handleReload} details={details} />
    );
  }
}

function buildErrorDetails(error, errorInfo) {
  const lines = [
    `url: ${typeof window !== "undefined" ? window.location?.href : "n/a"}`,
    `time: ${new Date().toISOString()}`,
    `name: ${error?.name || "Error"}`,
    `message: ${error?.message || String(error)}`,
  ];
  if (error?.stack) lines.push("", "stack:", error.stack);
  if (errorInfo?.componentStack) lines.push("", "componentStack:", errorInfo.componentStack);
  return lines.join("\n");
}

function ErrorDetailsBlock({ details }) {
  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="mt-4 text-left border border-border rounded-md bg-surface/50">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-foreground-muted hover:text-foreground"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Detalhes técnicos
        </button>
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px] text-foreground-muted hover:bg-surface-active hover:text-foreground transition-colors"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      {open && (
        <pre className="px-3 py-2 text-[11px] leading-relaxed text-foreground-secondary overflow-auto max-h-[260px] whitespace-pre-wrap break-words font-mono">
          {details}
        </pre>
      )}
    </div>
  );
}

function FullPageFallback({ onReload, details }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="mx-auto max-w-xl text-center">
        <AlertTriangle className="mx-auto h-16 w-16 text-destructive" />
        <h1 className="mt-6 text-2xl font-bold text-foreground">
          Algo deu errado
        </h1>
        <p className="mt-2 text-muted-foreground">
          Ocorreu um erro inesperado. Tente recarregar a página.
        </p>
        <button
          onClick={onReload}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Recarregar página
        </button>
        <ErrorDetailsBlock details={details} />
      </div>
    </div>
  );
}

function InlineFallback({ onReset, details }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="mx-auto max-w-xl text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
        <h2 className="mt-4 text-lg font-semibold text-foreground">
          Erro ao carregar esta página
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Tente novamente ou navegue para outra seção.
        </p>
        <button
          onClick={onReset}
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Tentar novamente
        </button>
        <ErrorDetailsBlock details={details} />
      </div>
    </div>
  );
}

export default ErrorBoundary;
